import type { PDFDocument, PDFRef } from "pdf-lib";

/**
 * Pieces shared by the site's Compress PDF and the API's black-and-white compress endpoints, so both write the same
 * kind of file and judge pages by the same rule.
 */

/** What a page's own dictionary, content stream and cross-reference entries cost once it is a picture. */
export const PAGE_OVERHEAD = 250;
/** A page becomes a picture only when that saves at least this share of what it costs now. */
export const MIN_PAGE_GAIN = 0.1;

/** True when `pictureBytes` (the picture's data plus its overhead) is clearly cheaper than the page as it stands. */
export function clearlySmaller(pictureBytes: number, pageFootprint: number): boolean {
  return pictureBytes < pageFootprint * (1 - MIN_PAGE_GAIN);
}

export interface Jbig2Piece { data: Uint8Array; w: number; h: number; globals?: Uint8Array }

/**
 * Adds a JBIG2 page picture to `out` as a one-bit DeviceGray /JBIG2Decode image and returns its reference. Symbol-mode
 * pages share one /JBIG2Globals stream; pass the same `globalsRefs` map for every page of the file so it is stored once.
 */
export function addJbig2Image(out: PDFDocument, jb2: Jbig2Piece, globalsRefs: Map<Uint8Array, PDFRef>): PDFRef {
  const dict: Record<string, unknown> = { Type: "XObject", Subtype: "Image", Width: jb2.w, Height: jb2.h, ColorSpace: "DeviceGray", BitsPerComponent: 1, Filter: "JBIG2Decode" };
  if (jb2.globals) {
    let g = globalsRefs.get(jb2.globals);
    if (!g) { g = out.context.register(out.context.stream(jb2.globals, {})); globalsRefs.set(jb2.globals, g); }
    dict.DecodeParms = { JBIG2Globals: g };
  }
  return out.context.register(out.context.stream(jb2.data, dict as never));
}
